import {
  Actions,
  Async,
  FSpread,
  SkipNull,
  Typed,
  TypedValue,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { TableListRow } from '../components/TableList';
import {
  SortGallerySelectedLabel,
  sortGalleryOptions,
  toFilesGallery,
  toFoldersGallery,
  toFileRow,
  typeToLabel,
  getFilesSchema,
  getFoldersSchema,
  toFolderRow,
  getFoldersBreadcrumbs,
} from './library.viewMappers';
import { useUpdate } from '@client/new/helpers/useUpdate';
import {
  Folder,
  Folders,
  Video,
  Videos,
  videoSourceType,
} from '../models/library.domain';
import { useVideoStateModalStore } from '@client/new/stores/videoStateModal.store';
import { useVideosWriteModel } from '../models/videos.model';
import { trackEvent } from '@services/tracking';
import { ReactNode, useEffect } from 'react';
import { FilterOption } from '@design-system/components/dropDown/dropDownFilter';
import toast from 'react-hot-toast';
import { useLibraryModel } from '../models/library.model';
import { ColumnSchema } from '@client/new/components/StandardTable/StandardTable';
import { isEmpty } from 'lodash';
import { FoldersGallery } from '../components/FoldersGalleryView';
import { FilesGallery } from '../components/FilesGalleryView';
import { UndoMove } from '../components/UndoMove';
import { VideoById } from '@client/new/common/videos/useUpload';
import { toDownloadUrl } from '@client/new/common/videos/mappers';
import {
  ContentFilters,
  DescriptionFilters,
  getFoldersScope,
  getVideosScope,
  SortFields,
  sortList,
  contentFilter,
  descriptionFilter,
  nameMatches,
} from '@client/new/common/library/helpers';

type FilterFields = 'content' | 'description';

type UndoMove = {
  id: number | null;
  selectedVideos: string[];
  selectedFolders: number[];
};

type PayloadMap = {
  close: never;
  search: string;
  edit: Video;
  delete: { id: string; videoSourceType: videoSourceType; name: string };
  download: never;
  openUploadModal: never;
  refreshList: never;
  closeConfirmation: never;
  confirmVideoDelete: never;
  viewExample: { exampleType: string };
  sortVideos: SortFields;
  sortFolders: SortFields;
  sortGallery: {
    field: SortFields;
    isAsc: boolean;
  };
  filterVideos: ContentFilters | DescriptionFilters;
  clearFilter: FilterFields;
  toogleGalleryView: never;
  clearSelectVideo: never;
  selectVideo: string;
  selectMultipleVideos: string;
  bulkDelete: never;
  confirmBulkDelete: never;
  selectAllVideo: never;

  deleteFolder: number;
  confirmFolderDelete: never;
  renameFolder: { name: string; id: number };
  renameFolderModal: Folder;
  openFolder: Folder;
  addFolderModal: never;
  addFolder: { name: string; parentId?: number };
  clearSelectFolder: never;
  selectFolder: number;
  selectMultipleFolders: number;
  moveToFolder: number | null;
  moveToFolderModal: never;
  selectAllFolders: never;
  undoMoveToFolder: UndoMove;

  breadcrumbClicked: Folder | null;
  clearAddFolderError: never;
  clearRenameFolderError: never;
  replaceVideoSuccess: VideoById;
  exportFiles: null;
  filterExtrasVideos: null;
};
type VideosVMAction = Actions<PayloadMap>;
export type VideosVMDispatcher = FSpread<PayloadMap>;

type MoveToFolder = {
  allFolders: Folder[];
  currentFolderId: number | null;
  currentFolderName?: string;
  movedItems: {
    count: number;
    folders: number[];
  };
};

type ModalType =
  | Typed<'None'>
  | TypedValue<'Edit', Video>
  | TypedValue<'DeleteVideoConfirmation'>
  | Typed<'BulkDeleteConfirmation'>
  | TypedValue<'DeleteFolderConfirmation', number>
  | Typed<'AddFolder'>
  | TypedValue<'RenameFolder', Folder>
  | TypedValue<'MoveToFolder', MoveToFolder>;

export type Breadcrumb = { label: string; onClick: () => void; id: number };

type VideosVMState = {
  searchText: string;
  openModal: ModalType;
  sortFieldFiles: SortFields;
  isAscFiles: boolean;
  sortFieldFolders: SortFields;
  isAscFolders: boolean;
  sortFieldGallery: SortFields;
  isAscGallery: boolean;
  contentFilter: ContentFilters | null;
  descriptionFilter: DescriptionFilters | null;
  galleryView: boolean;
  selectedVideos: string[];
  selectedFolders: number[];
  bulkDeleteProgress: boolean;
  openedFolderId: null | number;
  updateProgress: boolean;
  addFolderError?: string;
  renameFolderError?: string;
};

const initialState: VideosVMState = {
  searchText: '',
  openModal: { type: 'None' },
  sortFieldFiles: 'updatedAt',
  isAscFiles: false,
  sortFieldFolders: 'updatedAt',
  isAscFolders: false,
  sortFieldGallery: 'updatedAt',
  isAscGallery: false,
  contentFilter: null,
  descriptionFilter: null,
  galleryView: false,
  selectedVideos: [],
  selectedFolders: [],
  bulkDeleteProgress: false,
  openedFolderId: null,
  updateProgress: false,
  addFolderError: undefined,
  renameFolderError: undefined,
};

export type LibraryVM = VideosVMState & {
  creatorId: number;
  dispatch: VideosVMDispatcher;
  emptyLibrary: boolean;
  libraryError: boolean;
  influencerId: number;
  showGalleryView: boolean;
  showLoader: boolean;
  allFolders: Folders;
  allVideos: Videos;
  contentFilters: ContentFilters | null;
  files: {
    gallery: {
      data: FilesGallery;
      clearSelection: () => void;
    };
    listSelection: {
      allSelected: boolean;
      selectAll: () => void;
      onSelect: (value: string) => void;
      selectedIds: string[];
    };
    rows: Array<TableListRow>;
    schema: ColumnSchema[];
  };
  sortGallery: {
    options: any;
    label: string;
    selectedValue?: string;
    selectedLabel?: string | ReactNode;
    deleteSelection: () => void;
    selectSkipStyle: boolean;
  };
  folders: {
    gallery: {
      data: FoldersGallery;
      clearSelection: () => void;
    };
    listSelection: {
      allSelected: boolean;
      selectAll: () => void;
      onSelect: (value: number) => void;
      selectedIds: number[];
    };
    rows: Array<TableListRow>;
    schema: ColumnSchema[];
  };
  sort: {
    field: SortFields;
    isAsc: boolean;
    onClick: (field: SortFields) => void;
  };
  filters: Array<{
    options: Array<FilterOption>;
    label: string;
    selectedValue?: string;
    deleteSelection: () => void;
    className?: string;
  }>;
  bulkAction: {
    selectedCount: number;
    bulkDelete: () => void;
    moveToFolder: () => void;
  };
  breadcrumbs: Array<Breadcrumb>;
  filtersId: string;
};

export const useLibraryVM = (p: { influencerId: number }): Async<LibraryVM> => {
  const [state, updateState] = useUpdate(initialState);
  const { open: openUploadModal } = useVideoStateModalStore();
  const library = useLibraryModel({
    creatorId: p.influencerId,
  });
  const { deleteVideo, bulkDelete } = useVideosWriteModel({
    influencerId: p.influencerId,
  });

  useEffect(() => {
    if (sessionStorage.getItem('galleryView')) {
      updateState({ galleryView: true });
    }
  }, []);

  useEffect(() => {
    if (state.galleryView) {
      sessionStorage.setItem('galleryView', 'true');
    } else {
      sessionStorage.removeItem('galleryView');
    }
  }, [state.galleryView]);

  useEffect(() => {
    if (library.type === 'fetched' && window.location.hash) {
      const [hash, id] = window.location.hash.split('#');
      const folder = library.value.folders.find((i) => i.id === Number(id));

      if (folder && !state.openedFolderId) {
        updateState({
          openedFolderId: folder.id,
        });
      }
      if (!folder && !state.openedFolderId) {
        const [path] = window.location.pathname.split('#');
        window.history.replaceState(null, '', path);
      }
    }
  }, [library.type]);

  if (library.type !== 'fetched') return library;

  const { videos, folders, refetchLibraryContent } = library.value;

  const sortFieldFiles = state.galleryView
    ? state.sortFieldGallery
    : state.sortFieldFiles;
  const sortDirectionFiles = state.galleryView
    ? state.isAscGallery
    : state.isAscFiles;
  const sortFieldFolders = state.galleryView
    ? state.sortFieldGallery
    : state.sortFieldFolders;
  const sortDirectionFolders = state.galleryView
    ? state.isAscGallery
    : state.isAscFolders;

  const filteredVideos = getVideosScope(
    videos,
    state.openedFolderId,
    state.searchText,
  )
    .filter((value) => contentFilter(value, state.contentFilter))
    .filter((value) => descriptionFilter(value, state.descriptionFilter))
    .filter(nameMatches(state.searchText))
    .sort((a, b) => sortList(a, b, sortFieldFiles, sortDirectionFiles));

  const filteredFolders = getFoldersScope(
    folders,
    state.openedFolderId,
    state.searchText,
  )
    .filter(nameMatches(state.searchText))
    .sort((a, b) => sortList(a, b, sortFieldFolders, sortDirectionFolders));

  const dispatch: VideosVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as VideosVMAction;
    switch (action.type) {
      case 'close':
        return updateState({ openModal: { type: 'None' } });
      case 'search':
        return updateState({ searchText: action.payload });
      case 'edit':
        trackEvent({
          type: 'File Edited',
          payload: {
            'File Type': 'Video',
            'Edited From': 'Library',
            'Editing During': 'Post-upload',
            'File Length': typeToLabel(
              videos.find((video) => video.id === action.payload.id)?.type,
            ),
          },
        });
        return updateState({
          openModal: { type: 'Edit', value: action.payload },
        });
      case 'delete':
        return updateState({
          openModal: { type: 'DeleteVideoConfirmation', value: action.payload },
        });
      case 'download':
        trackEvent({
          type: 'File Downloaded',
          payload: { 'File Type': 'Video' },
        });
        return null;
      case 'closeConfirmation':
        return updateState({
          openModal: { type: 'None' },
          addFolderError: undefined,
          renameFolderError: undefined,
        });
      case 'confirmVideoDelete':
        deleteVideoConfirmation();
        return null;
      case 'openUploadModal':
        openUploadModal(p.influencerId, state.openedFolderId || null);
        return null;
      case 'refreshList':
        refetchLibraryContent();
        return null;
      case 'viewExample':
        trackEvent({
          type: 'Example Watched',
          payload: { 'Example Type': action.payload.exampleType },
        });
        return null;
      case 'sortVideos':
        return updateState({
          sortFieldFiles: action.payload,
          isAscFiles: !state.isAscFiles,
        });
      case 'sortFolders':
        return updateState({
          sortFieldFolders: action.payload,
          isAscFolders: !state.isAscFolders,
        });
      case 'sortGallery':
        return updateState({
          sortFieldGallery: action.payload.field,
          isAscGallery: action.payload.isAsc,
        });
      case 'filterVideos':
        if (action.payload == 'short' || action.payload === 'long') {
          updateState({ contentFilter: action.payload });
        }
        if (action.payload == 'yes' || action.payload === 'no') {
          updateState({ descriptionFilter: action.payload });
        }
        return null;
      case 'clearFilter':
        if (action.payload == 'content') {
          updateState({ contentFilter: null });
        }
        if (action.payload == 'description') {
          updateState({ descriptionFilter: null });
        }
        return null;
      case 'toogleGalleryView':
        return updateState({ galleryView: !state.galleryView });
      case 'clearSelectVideo':
        return updateState({
          selectedVideos: [],
        });
      case 'clearSelectFolder':
        return updateState({
          selectedFolders: [],
        });
      case 'selectVideo':
        return updateState({
          selectedVideos: [action.payload],
        });
      case 'selectMultipleVideos':
        if (state.selectedVideos.includes(action.payload)) {
          return updateState({
            selectedVideos: state.selectedVideos.filter(
              (i) => i !== action.payload,
            ),
          });
        }
        return updateState({
          selectedVideos: [...state.selectedVideos, action.payload],
        });
      case 'selectFolder':
        return updateState({
          selectedFolders: [action.payload],
        });
      case 'selectMultipleFolders':
        if (state.selectedFolders.includes(action.payload)) {
          return updateState({
            selectedFolders: state.selectedFolders.filter(
              (i) => i !== action.payload,
            ),
          });
        }
        return updateState({
          selectedFolders: [...state.selectedFolders, action.payload],
        });
      case 'selectAllFolders':
        return updateState({
          selectedFolders: [
            ...(state.selectedFolders.length === filteredFolders.length
              ? []
              : filteredFolders.map(({ id }) => id)),
          ],
        });
      case 'selectAllVideo':
        return updateState({
          selectedVideos: [
            ...(state.selectedVideos.length === filteredVideos.length
              ? []
              : filteredVideos.map(({ id }) => id)),
          ],
        });
      case 'bulkDelete':
        return updateState({
          openModal: { type: 'BulkDeleteConfirmation' },
        });
      case 'confirmBulkDelete':
        confirmBulkDelete();
        return null;
      case 'moveToFolderModal':
        return updateState({
          openModal: {
            type: 'MoveToFolder',
            value: {
              allFolders: library.value.folders,
              currentFolderId: state.openedFolderId,
              currentFolderName: library.value.folders.find(
                (i) => i.id === state.openedFolderId,
              )?.name,
              movedItems: {
                count:
                  state.selectedFolders.length + state.selectedVideos.length,
                folders: state.selectedFolders,
              },
            },
          },
        });
      case 'moveToFolder':
        return moveToFolder(action.payload);
      case 'deleteFolder':
        return updateState({
          openModal: {
            type: 'DeleteFolderConfirmation',
            value: action.payload,
          },
        });
      case 'confirmFolderDelete':
        if (state.openModal.type === 'DeleteFolderConfirmation') {
          updateState({
            bulkDeleteProgress: true,
          });
          library.value
            .deleteFolder({ id: state.openModal.value })
            .then(() => {
              toast.success('Content deleted successfully');
              refetchLibraryContent();
            })
            .catch((e) => {
              toast.error(e.message);
            })
            .finally(() => {
              updateState({
                bulkDeleteProgress: false,
              });
            });
          updateState({
            selectedVideos: [],
            openModal: { type: 'None' },
          });
        }
        return null;
      case 'openFolder':
        updateFolderHash(action.payload.id);
        return updateState({
          openedFolderId: action.payload.id,
          selectedFolders: [],
          selectedVideos: [],
        });
      case 'renameFolderModal':
        return updateState({
          openModal: { type: 'RenameFolder', value: action.payload },
        });
      case 'renameFolder':
        updateState({
          updateProgress: true,
        });
        library.value
          .renameFolder(action.payload)
          .then((res) => {
            if (res.data) {
              toast.success('Folder renamed successfully');
              library.value.refetchLibraryContent();
              updateState({
                openModal: { type: 'None' },
              });
            }
          })
          .catch((er) => {
            updateState({
              renameFolderError: er.message,
            });
          })
          .finally(() => {
            updateState({
              updateProgress: false,
            });
          });
        return null;
      case 'addFolder':
        library.value
          .addFolder(action.payload)
          .then(() => {
            toast.success('Folder was added succesfully');
            library.value.refetchLibraryContent();
            updateState({
              openModal: { type: 'None' },
            });
          })
          .catch((er) => {
            updateState({
              addFolderError: er.message,
            });
          });
        return null;
      case 'addFolderModal':
        return updateState({
          openModal: { type: 'AddFolder' },
        });
      case 'breadcrumbClicked':
        updateFolderHash(action.payload?.id);
        return updateState({
          openedFolderId: action.payload?.id || null,
          selectedFolders: [],
          selectedVideos: [],
        });
      case 'undoMoveToFolder':
        return undoMoveToFolder(action.payload);
      case 'clearAddFolderError':
        return updateState({
          addFolderError: undefined,
        });
      case 'clearRenameFolderError':
        return updateState({
          renameFolderError: undefined,
        });
      case 'replaceVideoSuccess':
        refetchLibraryContent();
        if (!action.payload) return null;
        return updateState({
          openModal: { type: 'Edit', value: videoByIdToVideo(action.payload) },
        });
      case 'exportFiles':
        downloadCSV(filteredVideos.map((v) => [v.name, v.description || '']));
        return null;
      case 'filterExtrasVideos':
        if (state.contentFilter === 'extras-video') {
          updateState({ contentFilter: null });
        } else {
          updateState({ contentFilter: 'extras-video' });
        }
    }
  };

  const videoByIdToVideo = (v: SkipNull<VideoById>): Video => {
    return {
      creatorId: v.creatorId,
      id: v.uuid,
      videoSourceType: v.videoSourceType as videoSourceType,
      type: v.type || '',

      url: v.videoUrl || '',
      downloadUrl: toDownloadUrl(v.downloadLink),
      thumbnailUrl: v.videoThumbnailUrl || '',

      name: v.title,
      description: v.description || undefined,
      createdAt: v.createdAt,
      updatedAt: v.updatedAt,

      width: v.width || 0,
      height: v.height || 0,
      duration: v.duration || 0,
      fileSize: v.fileSize || 0,
      folderId: v.folderId || null,

      isProcessing: v.status !== 'PROCESSING_FINISHED',
    };
  };

  const moveToFolder = (folderId: number | null) => {
    const undoMove = {
      id: state.openedFolderId,
      selectedVideos: state.selectedVideos,
      selectedFolders: state.selectedFolders.map((i) => Number(i)),
    };

    updateState({ updateProgress: true });
    library.value
      .addToFolder({
        id: folderId,
        videoIds: state.selectedVideos,
        folderIds: state.selectedFolders.map((i) => Number(i)),
      })
      .then(() => {
        updateState({
          selectedVideos: [],
          selectedFolders: [],
        });
        UndoMove({
          itemsCount:
            state.selectedVideos.length + state.selectedFolders.length,
          fromName:
            folders.find((i) => i.id === state.openedFolderId)?.name ||
            'Library',
          toName: folders.find((i) => i.id === folderId)?.name || 'Library',
          undo: () => dispatch('undoMoveToFolder', undoMove),
        });
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        updateState({
          openModal: { type: 'None' },
          updateProgress: false,
        });
        refetchLibraryContent();
      });
    return null;
  };

  const undoMoveToFolder = (undoMove: UndoMove) => {
    updateState({ updateProgress: true });
    library.value
      .addToFolder({
        id: undoMove.id,
        videoIds: undoMove.selectedVideos,
        folderIds: undoMove.selectedFolders.map((i) => Number(i)),
      })
      .then(() => {
        toast.success('Items were moved back successfully');
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        updateState({
          updateProgress: false,
        });
        refetchLibraryContent();
      });
    return null;
  };

  const confirmBulkDelete = () => {
    updateState({
      bulkDeleteProgress: true,
    });
    bulkDelete(state.selectedVideos, state.selectedFolders)
      .then(() => {
        toast.success('Content deleted successfully');
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => {
        refetchLibraryContent();
        updateState({
          selectedVideos: [],
          selectedFolders: [],
          openModal: { type: 'None' },
          bulkDeleteProgress: false,
        });
      });
  };

  const deleteVideoConfirmation = () => {
    if (state.openModal.type === 'DeleteVideoConfirmation') {
      const variables = state.openModal.value;

      trackEvent({
        type: 'File Deleted',
        payload: {
          'File Type': 'Video',
          'Deleted During': 'Post-upload',
          'File Length': typeToLabel(
            videos.find((video) => video.id === variables.id)?.type,
          ),
        },
      });

      deleteVideo(variables)
        .then(() => {
          toast.success('Content deleted successfully');
        })
        .catch((e) => {
          toast.error(e.message);
        });
      library.value.refetchLibraryContent();
      updateState({
        openModal: { type: 'None' },
      });
    }
  };

  const updateFolderHash = (id?: number) => {
    if (!id && window.location.hash) {
      const updatedPath = window.location.pathname.split('#')[0];
      window.history.pushState(null, '', updatedPath);
      return;
    }
    if (!id) return;
    if (window.location.hash !== '#' + id)
      window.history.pushState(null, '', window.location.pathname + '#' + id);
  };

  const fileRows = filteredVideos.map(toFileRow(dispatch));
  const folderRows = filteredFolders.map(toFolderRow(dispatch));

  const filesGallery = filteredVideos.map(
    toFilesGallery(dispatch, state.selectedVideos),
  );
  const foldersGallery = filteredFolders.map(
    toFoldersGallery(dispatch, state.selectedFolders),
  );

  const selectedCount =
    state.selectedVideos.length + state.selectedFolders.length;

  return toAsyncFetched<LibraryVM>({
    ...state,
    creatorId: p.influencerId,
    dispatch,
    emptyLibrary: !state.openedFolderId && isEmpty(videos) && isEmpty(folders),
    libraryError: true,
    allFolders: library.value.folders,
    allVideos: library.value.videos,
    folders: {
      rows: folderRows,
      schema: getFoldersSchema({
        onClick: (value: Folder) => dispatch('openFolder', value),
        sort: (field: SortFields) => dispatch('sortFolders', field),
        field: state.sortFieldFolders,
        isAsc: state.isAscFolders,
      }),
      gallery: {
        data: foldersGallery,
        clearSelection: () => dispatch('clearSelectFolder'),
      },
      listSelection: {
        allSelected: state.selectedFolders.length === filteredFolders.length,
        selectAll: () => dispatch('selectAllFolders'),
        onSelect: (value: number) => dispatch('selectMultipleFolders', value),
        selectedIds: state.selectedFolders,
      },
    },
    files: {
      rows: fileRows,
      gallery: {
        data: filesGallery,
        clearSelection: () => dispatch('clearSelectVideo'),
      },
      listSelection: {
        allSelected: state.selectedVideos.length === filteredVideos.length,
        selectAll: () => dispatch('selectAllVideo'),
        onSelect: (value: string) => dispatch('selectMultipleVideos', value),
        selectedIds: state.selectedVideos,
      },
      schema: getFilesSchema({
        onClick: (field: SortFields) => dispatch('sortVideos', field),
        field: state.sortFieldFiles,
        isAsc: state.isAscFiles,
      }),
    },
    sortGallery: {
      options: sortGalleryOptions(dispatch),
      label: 'Sort by',
      selectedLabel: SortGallerySelectedLabel({
        type: state.sortFieldGallery,
        isAsc: state.isAscGallery,
      }),
      deleteSelection: () =>
        dispatch('sortGallery', { field: 'updatedAt', isAsc: false }),
      selectSkipStyle: true,
    },
    influencerId: p.influencerId,
    showGalleryView: state.galleryView,
    showLoader: state.bulkDeleteProgress,
    bulkAction: {
      selectedCount,
      bulkDelete: () => dispatch('bulkDelete'),
      moveToFolder: () => dispatch('moveToFolderModal'),
    },
    breadcrumbs: getFoldersBreadcrumbs({
      allFolders: folders,
      openedFolderId: state.openedFolderId,
      dispatch,
    }),
    sort: {
      onClick: (field: SortFields) => dispatch('sortVideos', field),
      field: state.sortFieldFiles,
      isAsc: state.isAscFiles,
    },
    filters: [
      {
        options: [
          {
            label: 'Clip',
            value: 'short',
            onClick: ({ value }: FilterOption) =>
              dispatch(
                'filterVideos',
                value as DescriptionFilters | ContentFilters,
              ),
          },
          {
            label: 'Video',
            value: 'long',
            onClick: ({ value }: FilterOption) =>
              dispatch(
                'filterVideos',
                value as DescriptionFilters | ContentFilters,
              ),
          },
        ],
        label: 'Content type',
        selectedValue: state.contentFilter || undefined,
        deleteSelection: () => dispatch('clearFilter', 'content'),
      },
      {
        options: [
          {
            label: 'With description',
            value: 'yes',
            selectedLabel: 'Description: Yes',
            onClick: ({ value }: FilterOption) =>
              dispatch(
                'filterVideos',
                value as DescriptionFilters | ContentFilters,
              ),
          },
          {
            label: 'Without description',
            value: 'no',
            selectedLabel: 'Description: No',
            onClick: ({ value }: FilterOption) =>
              dispatch(
                'filterVideos',
                value as DescriptionFilters | ContentFilters,
              ),
          },
        ],
        label: 'Description',
        selectedValue: state.descriptionFilter || undefined,
        deleteSelection: () => dispatch('clearFilter', 'description'),
      },
    ],
    contentFilters: state.contentFilter,
    filtersId: 'librarySearchFilters',
  });
};

export const downloadCSV = (data: Array<[string, string]>) => {
  const headers = ['Title', 'Description'];

  const rows = data.map(([title, desc]) => [
    `"${title.replace(/"/g, '""')}"`,
    `"${desc.replace(/"/g, '""')}"`,
  ]);

  const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');

  link.download = `exercises-list-${yyyy}-${mm}-${dd}.csv`;
  link.href = url;
  link.click();

  URL.revokeObjectURL(url);
};
